import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import localforage from 'localforage';
import { Link } from 'react-router-dom';
import { useBaseUrl } from '../../utils/utils';
import { showToast } from '../../utils/swal';

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

type StaffView = 'overview' | 'directory' | 'create';

type StaffProps = {
    initialView?: StaffView;
};

type StaffMember = {
    user_id?: number;
    fname: string;
    lname: string;
    email: string;
    title?: string;
    avatar?: string;
    status?: boolean;
    clinic?: string;
    clinic_id?: number;
};

type StaffTitle = {
    id: number;
    name: string;
    description?: string;
};

type CountyOption = {
    county: string;
    county_code: number;
};

type TownOption = {
    town: string;
    town_id: number;
};

const Staff: React.FC<StaffProps> = ({ initialView = 'overview' }) => {
    const baseUrl = useBaseUrl();
    const [view, setView] = useState<StaffView>(initialView);
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [clinicName, setClinicName] = useState('Current Facility');
    const [staffId, setStaffId] = useState<number | null>(null);
    const [institutionId, setInstitutionId] = useState<number | null>(null);
    const [scope, setScope] = useState<'current' | 'network'>('current');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
    const [titles, setTitles] = useState<StaffTitle[]>([]);
    const [counties, setCounties] = useState<CountyOption[]>([]);
    const [towns, setTowns] = useState<TownOption[]>([]);
    const [search, setSearch] = useState('');
    const [form, setForm] = useState({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phone: '',
        title: '',
        county: '',
        town: '',
        password: '',
        confirmPassword: '',
    });

    useEffect(() => {
        setView(initialView);
    }, [initialView]);

    useEffect(() => {
        const loadContext = async () => {
            const [currentClinicId, currentClinicName, currentStaffId, currentInstitutionId] = await Promise.all([
                localforage.getItem<number>('clinic_id'),
                localforage.getItem<string>('clinic_name'),
                localforage.getItem<number>('staff_id'),
                localforage.getItem<number>('institution_id'),
            ]);

            setClinicId(currentClinicId || null);
            setClinicName(currentClinicName || 'Current Facility');
            setStaffId(currentStaffId || null);
            setInstitutionId(currentInstitutionId || null);
        };

        loadContext();
    }, []);

    useEffect(() => {
        if (!institutionId) return;

        const loadData = async () => {
            setIsLoading(true);
            try {
                const [staffResponse, titlesResponse, countiesResponse] = await Promise.all([
                    axios.post(`${baseUrl}facilities/clinic-list`, { institution_id: institutionId }, { timeout: 12000 }),
                    axios.get(`${baseUrl}facilities/staff-titles`, { timeout: 12000 }),
                    axios.post(`${baseUrl}facilities/get-counties`, new FormData(), { timeout: 12000 }),
                ]);

                if (staffResponse.data?.status === 'success' && Array.isArray(staffResponse.data.data)) {
                    setStaffMembers(staffResponse.data.data);
                } else {
                    setStaffMembers([]);
                }

                if (titlesResponse.data?.status === 'success' && Array.isArray(titlesResponse.data.data)) {
                    setTitles(titlesResponse.data.data.map((item: any) => ({ id: item.id, name: item.name, description: item.description })));
                } else {
                    setTitles([]);
                }

                if (countiesResponse.data?.status === 'success' && Array.isArray(countiesResponse.data.data)) {
                    setCounties(countiesResponse.data.data);
                } else {
                    setCounties([]);
                }
            } catch (error) {
                console.error('Failed to load staff workspace:', error);
                setStaffMembers([]);
                setTitles([]);
                setCounties([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [baseUrl, institutionId]);

    useEffect(() => {
        const loadTowns = async () => {
            if (!form.county) {
                setTowns([]);
                return;
            }

            try {
                const response = await axios.post(`${baseUrl}facilities/get-towns`, { county_id: Number(form.county) }, { timeout: 12000 });
                if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
                    setTowns(response.data.data);
                } else {
                    setTowns([]);
                }
            } catch (error) {
                console.error('Failed to load towns:', error);
                setTowns([]);
            }
        };

        loadTowns();
    }, [baseUrl, form.county]);

    const visibleStaff = useMemo(() => {
        const base = scope === 'network' ? staffMembers : staffMembers.filter((item) => Number(item.clinic_id) === Number(clinicId));
        const term = search.trim().toLowerCase();

        if (!term) return base;

        return base.filter((item) => {
            const haystack = [item.fname, item.lname, item.email, item.title, item.clinic]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(term);
        });
    }, [clinicId, scope, search, staffMembers]);

    const stats = useMemo(() => {
        const currentFacilityStaff = staffMembers.filter((item) => Number(item.clinic_id) === Number(clinicId));
        return {
            current: currentFacilityStaff.length,
            network: staffMembers.length,
            active: currentFacilityStaff.filter((item) => Boolean(item.status)).length,
            pending: currentFacilityStaff.filter((item) => !item.status).length,
        };
    }, [clinicId, staffMembers]);

    const updateForm = (field: keyof typeof form, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const resetForm = () => {
        setForm({
            firstName: '',
            middleName: '',
            lastName: '',
            email: '',
            phone: '',
            title: '',
            county: '',
            town: '',
            password: '',
            confirmPassword: '',
        });
        setTowns([]);
    };

    const reloadStaff = async () => {
        if (!institutionId) return;
        try {
            const response = await axios.post(`${baseUrl}facilities/clinic-list`, { institution_id: institutionId }, { timeout: 12000 });
            if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
                setStaffMembers(response.data.data);
            }
        } catch (error) {
            console.error('Failed to refresh staff list:', error);
        }
    };

    const submitStaff = async () => {
        if (!clinicId) {
            showToast({ icon: 'warning', title: 'No active facility', text: 'Switch to a facility before adding staff.' });
            return;
        }

        if (!form.firstName || !form.lastName || !form.email || !form.title || !form.town || !form.password) {
            showToast({ icon: 'warning', title: 'Missing details', text: 'Complete the required staff fields before submitting.' });
            return;
        }

        if (form.password !== form.confirmPassword) {
            showToast({ icon: 'warning', title: 'Passwords do not match', text: 'Review the password fields and try again.' });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = new FormData();
            payload.append('title', form.title);
            payload.append('clinic_id', String(clinicId));
            payload.append('first_name', form.firstName);
            payload.append('middle_name', form.middleName);
            payload.append('last_name', form.lastName);
            payload.append('town_id', form.town);
            payload.append('email', form.email);
            payload.append('password', form.password);
            payload.append('phone', form.phone);

            const response = await axios.post(`${baseUrl}facilities/add-staff`, payload, { timeout: 15000 });
            if (response.data?.status === 'success') {
                showToast({ icon: 'success', title: 'Staff created', text: `${form.firstName} ${form.lastName} has been added to ${clinicName}.` });
                resetForm();
                await reloadStaff();
                setView('directory');
                return;
            }

            showToast({ icon: 'error', title: 'Unable to create staff', text: response.data?.message || 'Please try again.' });
        } catch (error: any) {
            console.error('Failed to create staff:', error);
            showToast({ icon: 'error', title: 'Staff creation failed', text: error?.response?.data?.message || 'The request could not be completed.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="sc-card" style={{ margin: 0 }}>
                <div className="sc-card-body" style={{ padding: '18px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                        <div>
                            <div style={{ fontSize: '0.76rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: ACCENT }}>Staff Management</div>
                            <div style={{ fontSize: '0.92rem', fontWeight: 700, marginTop: 4 }}>Manage onboarding, current staff visibility, and access for {clinicName}</div>
                            <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)', marginTop: 4 }}>Current facility context updates when you switch facilities from the manager header.</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {(['overview', 'directory', 'create'] as StaffView[]).map((item) => (
                                <button
                                    key={item}
                                    className={item === view ? 'sc-btn sc-btn-primary sc-btn-sm' : 'sc-btn sc-btn-ghost sc-btn-sm'}
                                    onClick={() => setView(item)}
                                >
                                    {item === 'overview' ? 'Overview' : item === 'directory' ? 'Staff Directory' : 'Add Staff'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="sc-stats" style={{ marginBottom: 0 }}>
                        <div className="sc-stat">
                            <div className="sc-stat-icon teal"><i className="bx bx-user-plus"></i></div>
                            <div className="sc-stat-value">{stats.current}</div>
                            <div className="sc-stat-label">Current Facility</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon blue"><i className="bx bx-buildings"></i></div>
                            <div className="sc-stat-value">{stats.network}</div>
                            <div className="sc-stat-label">Institution Network</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon amber"><i className="bx bx-badge-check"></i></div>
                            <div className="sc-stat-value">{stats.active}</div>
                            <div className="sc-stat-label">Active</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon violet"><i className="bx bx-time"></i></div>
                            <div className="sc-stat-value">{stats.pending}</div>
                            <div className="sc-stat-label">Pending Verification</div>
                        </div>
                    </div>
                </div>
            </div>

            {view === 'overview' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div className="sc-card" style={{ margin: 0 }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--uzima-shell-border)', fontWeight: 700, fontSize: '0.82rem' }}>
                            <i className="bx bx-layer me-2" style={{ color: ACCENT }}></i>Workspace Summary
                        </div>
                        <div className="sc-card-body" style={{ display: 'grid', gap: 10 }}>
                            {[
                                { label: 'Current facility', value: clinicName },
                                { label: 'Visible staff by default', value: `${stats.current} team members` },
                                { label: 'Institution network reach', value: `${stats.network} staff records` },
                                { label: 'Best next action', value: stats.current === 0 ? 'Add your first staff member' : 'Review current team access' },
                            ].map((item) => (
                                <div key={item.label} style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(15,23,42,0.03)' }}>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--uzima-shell-text-muted)', fontWeight: 700 }}>{item.label}</div>
                                    <div style={{ marginTop: 4, fontSize: '0.82rem', fontWeight: 700 }}>{item.value}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="sc-card" style={{ margin: 0 }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--uzima-shell-border)', fontWeight: 700, fontSize: '0.82rem' }}>
                            <i className="bx bx-rocket me-2" style={{ color: '#3b82f6' }}></i>Quick Actions
                        </div>
                        <div className="sc-card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => setView('create')}><i className="bx bx-user-plus"></i> Add Staff</button>
                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setView('directory')}><i className="bx bx-list-ul"></i> Browse Directory</button>
                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setScope('current')}><i className="bx bx-home-alt"></i> Current Facility</button>
                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setScope('network')}><i className="bx bx-buildings"></i> Institution Scope</button>
                            <Link className="sc-btn sc-btn-ghost sc-btn-sm" to="/institution/suspend-staff"><i className="bx bx-user-x"></i> Suspend / Reactivate</Link>
                        </div>
                        <div style={{ padding: '0 18px 18px', fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)' }}>
                            Use the directory view for discovery and the add staff view for onboarding. Newly added staff stay pending until verification is completed.
                        </div>
                    </div>
                </div>
            )}

            {view === 'directory' && (
                <div className="sc-card" style={{ margin: 0 }}>
                    <div className="sc-card-body" style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                            <div className="sc-search" style={{ marginBottom: 0, flex: 1, minWidth: 260 }}>
                                <i className="bx bx-search"></i>
                                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, title, or facility..." />
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button className={scope === 'current' ? 'sc-btn sc-btn-primary sc-btn-sm' : 'sc-btn sc-btn-ghost sc-btn-sm'} onClick={() => setScope('current')}>
                                    Current Facility
                                </button>
                                <button className={scope === 'network' ? 'sc-btn sc-btn-primary sc-btn-sm' : 'sc-btn sc-btn-ghost sc-btn-sm'} onClick={() => setScope('network')}>
                                    Institution Network
                                </button>
                                <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={reloadStaff}><i className="bx bx-refresh"></i> Refresh</button>
                            </div>
                        </div>

                        {isLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '1.8rem', color: ACCENT }}></i>
                                <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)' }}>Loading staff directory...</div>
                            </div>
                        ) : visibleStaff.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '36px 20px' }}>
                                <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 14px', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="bx bx-user-x" style={{ color: ACCENT, fontSize: '1.4rem' }}></i>
                                </div>
                                <div style={{ fontWeight: 700, marginBottom: 6 }}>No staff found</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)' }}>Adjust the search or switch scope to view a wider staff list.</div>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                                {visibleStaff.map((member, index) => (
                                    <div key={`${member.email}-${index}`} style={{ border: '1px solid var(--uzima-shell-border)', borderRadius: 16, padding: 16, background: '#fff' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                            <div style={{ width: 44, height: 44, borderRadius: 14, background: `linear-gradient(135deg, ${ACCENT}, #14b8a6)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                                                {`${member.fname?.charAt(0) || ''}${member.lname?.charAt(0) || ''}`}
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{member.fname} {member.lname}</div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>{member.email}</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gap: 8, fontSize: '0.74rem' }}>
                                            <div><strong>Role:</strong> {member.title || 'Staff'}</div>
                                            <div><strong>Facility:</strong> {member.clinic || clinicName}</div>
                                            <div>
                                                <strong>Status:</strong>{' '}
                                                <span style={{ padding: '3px 8px', borderRadius: 999, background: member.status ? ACCENT_SOFT : 'rgba(245,158,11,0.14)', color: member.status ? ACCENT : '#b45309', fontWeight: 700, fontSize: '0.66rem' }}>
                                                    {member.status ? 'Active' : 'Pending'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {view === 'create' && (
                <div className="sc-card" style={{ margin: 0 }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--uzima-shell-border)', fontWeight: 700, fontSize: '0.82rem' }}>
                        <i className="bx bx-user-plus me-2" style={{ color: ACCENT }}></i>Add Staff To {clinicName}
                    </div>
                    <div className="sc-card-body" style={{ display: 'grid', gap: 14 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                            <div>
                                <label className="form-label">First Name</label>
                                <input className="form-control" value={form.firstName} onChange={(e) => updateForm('firstName', e.target.value)} />
                            </div>
                            <div>
                                <label className="form-label">Middle Name</label>
                                <input className="form-control" value={form.middleName} onChange={(e) => updateForm('middleName', e.target.value)} />
                            </div>
                            <div>
                                <label className="form-label">Last Name</label>
                                <input className="form-control" value={form.lastName} onChange={(e) => updateForm('lastName', e.target.value)} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <label className="form-label">Email</label>
                                <input type="email" className="form-control" value={form.email} onChange={(e) => updateForm('email', e.target.value)} />
                            </div>
                            <div>
                                <label className="form-label">Phone</label>
                                <input className="form-control" value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                            <div>
                                <label className="form-label">Staff Title</label>
                                <select className="form-control" value={form.title} onChange={(e) => updateForm('title', e.target.value)}>
                                    <option value="">Select title</option>
                                    {titles.map((title) => (
                                        <option key={title.id} value={title.id}>{title.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="form-label">County</label>
                                <select className="form-control" value={form.county} onChange={(e) => { updateForm('county', e.target.value); updateForm('town', ''); }}>
                                    <option value="">Select county</option>
                                    {counties.map((county) => (
                                        <option key={county.county_code} value={county.county_code}>{county.county}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="form-label">Town</label>
                                <select className="form-control" value={form.town} disabled={towns.length === 0} onChange={(e) => updateForm('town', e.target.value)}>
                                    <option value="">Select town</option>
                                    {towns.map((town) => (
                                        <option key={town.town_id} value={town.town_id}>{town.town}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <label className="form-label">Password</label>
                                <input type="password" className="form-control" value={form.password} onChange={(e) => updateForm('password', e.target.value)} />
                            </div>
                            <div>
                                <label className="form-label">Confirm Password</label>
                                <input type="password" className="form-control" value={form.confirmPassword} onChange={(e) => updateForm('confirmPassword', e.target.value)} />
                            </div>
                        </div>

                        <div style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(59,130,246,0.06)', fontSize: '0.74rem', color: 'var(--uzima-shell-text-soft)' }}>
                            New staff records are created under the active facility and remain pending until the platform verification flow completes.
                        </div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button className="sc-btn sc-btn-primary" disabled={isSubmitting} onClick={submitStaff}>
                                <i className={isSubmitting ? 'bx bx-loader-alt bx-spin' : 'bx bx-user-plus'}></i>
                                {isSubmitting ? ' Creating Staff...' : ' Create Staff'}
                            </button>
                            <button className="sc-btn sc-btn-ghost" onClick={resetForm}><i className="bx bx-reset"></i> Reset</button>
                            <Link to="/institution/clinics" className="sc-btn sc-btn-ghost" style={{ textDecoration: 'none' }}><i className="bx bx-buildings"></i> Institution Management</Link>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Staff
